<template>
  <div class="tw-relative tw-z-50">
    <NavigationTopBar class="fast-default-header-top-bar tw-hidden md:tw-flex" />
    <header
      data-allow-mismatch
      :elevated="!isScrollTop"
      class="fast-header tw-inset-x-0 tw-top-0"
      :class="headerClasses"
    >
      <QToolbar
        class="fast-default-toolbar tw-h-16 md:tw-h-20 tw-bg-white sm:!tw-px-0"
        :class="{
          'md:tw-rounded-xl': isScrollTop,
          'md:!tw-px-0': isHomepage,
          'md:!tw-px-4': !isHomepage,
        }"
      >
        <div
          class="tw-w-full tw-h-[40px] md:tw-h-full tw-flex tw-items-center tw-px-0 md:tw-px-4 tw-container"
        >
          <!-- Logo -->
          <NavigationLogo />

          <!-- Spacer -->
          <div class="tw-flex-1" />

          <!-- Right: Navigation + Mobile Menu -->
          <div class="tw-flex tw-items-center tw-h-full">
            <nav class="tw-hidden xl:tw-flex tw-items-center tw-h-full">
              <NavigationMenu
                class="tw-h-full"
                menu-text-class="tw-text-mb-body tw-text-dt-title-5"
                show-submenu-bottom-bar
                submenu-rounded
                :hide-submenu-border="isFixedFloat"
              />
            </nav>

            <QBtn
              flat
              round
              dense
              :ripple="false"
              icon="menu"
              class="tw-block xl:tw-hidden"
              @click="emit('click-mobile-menu')"
            />
          </div>
        </div>
      </QToolbar>
    </header>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  isHomepage: {
    type: Boolean,
    default: false,
  },
});

const themeStyleStore = useThemeStyleStore();
const { scrollY } = useHydrationSafeScroll();
const isScrollTop = computed(() => scrollY.value === 0);
const isTopBarAreaVisible = computed(() => scrollY.value < 40); // TopBar height is 40px (tw-h-10)
const isFixedFloat = computed(() => themeStyleStore.themeId === 3); // Check if themeId is Creative theme

// Computed properties for header styling
const headerClasses = computed(() => ({
  // Fixed for all states except Creative theme at scroll top
  'tw-fixed tw-z-50': !(isFixedFloat.value && isScrollTop.value),

  // Creative theme at scroll top scrolls with the page (relative)
  'tw-relative': isFixedFloat.value && isScrollTop.value,

  // Desktop floating card: container-width, rounded, below TopBar (64px from top), shadow.
  // Banner overlays behind via negative margin-top. Only at scroll top on homepage.
  'md:tw-container md:!tw-px-0 md:tw-rounded-xl md:tw-top-16 fast-header-floating':
    props.isHomepage && isScrollTop.value && !isFixedFloat.value,

  // Full width for non-homepage or Creative theme
  'tw-w-full': !props.isHomepage || isFixedFloat.value,

  // Flush top-0 for non-homepage or Creative theme at scroll top
  '!tw-top-0 !tw-rounded-none':
    !props.isHomepage || (isFixedFloat.value && isScrollTop.value),

  // Desktop: position below TopBar (40px) while TopBar is still visible
  'md:!tw-top-10':
    !props.isHomepage && isTopBarAreaVisible.value && !isFixedFloat.value,

  // Slide-in animation when homepage header transitions from floating → fixed
  'header-fixed-animate':
    props.isHomepage && !isScrollTop.value && !isFixedFloat.value,
}));

const emit = defineEmits(['click-mobile-menu']);
</script>

<style>
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translate3d(0, -100%, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.fast-header,
.fast-header .q-toolbar {
  -webkit-tap-highlight-color: transparent;
  outline: none;
}

.fast-default-header-top-bar.fast-default-top-bar {
  border-bottom-color: color-mix(
    in srgb,
    var(--theme-color-light-gray-2) 50%,
    transparent
  ) !important;
}

@media (max-width: 767px) {
  .fast-default-toolbar.q-toolbar {
    padding: 12px 16px !important;
  }

  /* Prevent tw-container from constraining toolbar content on mobile */
  .fast-default-toolbar .tw-container {
    max-width: 100% !important;
  }
}

@media (min-width: 768px) {
  /* Animation only at md+ where floating → fixed transition occurs */
  .header-fixed-animate {
    animation: 900ms cubic-bezier(0.2, 1, 0.22, 1) 0s normal none 1 running
      fadeInDown;
  }

  /* Drop shadow matching Figma design for the floating navbar card */
  .fast-header-floating {
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
  }

  .fast-header.q-header {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
</style>
